import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { Change, Project, Sprint } from '../../models';
import { ChangeCard, ChangeModal, Icon, Select, WidgetSkeleton } from '..';
import type { Value } from '../Select';
import {
    currentSprint,
    loadChanges,
    resetChanges,
    useAppDispatch,
    useAppSelector,
} from '../../redux';
import {
    DEFAULT_MY_TASKS_SORT,
    loadMyTasksSortMode,
    MyTasksSortMode,
    saveMyTasksSortMode,
} from '../../utils/myTasksFocusPrefs';
import { useDashboardChangeModal } from '../../utils/useDashboardChangeModal';
import {
    FOLLOW_STATUSES,
    isClientReplyPending,
    isFollowStatus,
    isMyTasksListLoading,
    isMyTasksVisible,
    isMyTasksWidgetRelevant,
    isTodoStatus,
    MY_TASKS_FETCH_STATUSES,
    NON_DEV_PIPELINE_STATUSES,
    TODO_STATUSES,
} from '../../utils/myTasksFocusVisibility';
import {
    compareAllChanges,
    compareByCreatedAt,
    compareByPriority,
    compareByUpdatedAt,
    compareOverdueChanges,
    compareTodoChanges,
    isDeadlineOverdue,
} from '../../utils/myTasksFocusSort';

type TasksTab = 'replies' | 'todo' | 'follow' | 'sprint' | 'overdue' | 'all';

const TABS: TasksTab[] = ['replies', 'todo', 'follow', 'sprint', 'overdue', 'all'];
const SORT_MODES: MyTasksSortMode[] = ['intelligent', 'priority', 'updated', 'newest', 'oldest'];

function myTasksListParams(assigneeId: string) {
    return {
        assignee: assigneeId,
        'progress.status': MY_TASKS_FETCH_STATUSES.join(','),
        // Include finished/deployed (etc.) only when clientReplyPending is set,
        // instead of downloading the whole terminal history for the assignee.
        or_client_reply_pending: '1',
    };
}

function buildBulkActionsHref(args: {
    assigneeId: string;
    tab: TasksTab;
    projectFilter: Value;
    typeFilter: Value;
}): string {
    const params = new URLSearchParams();
    params.set('assignee', args.assigneeId);
    if (args.projectFilter !== 'all') {
        params.set('project', String(args.projectFilter));
    }
    if (args.typeFilter !== 'all') {
        params.set('type', String(args.typeFilter));
    }
    if (args.tab === 'todo') {
        params.set('status', [...TODO_STATUSES, ...NON_DEV_PIPELINE_STATUSES].join(','));
    } else if (args.tab === 'follow') {
        params.set('status', FOLLOW_STATUSES.join(','));
    }
    return `/changes?${params.toString()}`;
}

function isWaitingOnOthers(change: Change): boolean {
    return isFollowStatus(change);
}

function isSprintSlip(change: Change, currentSprintId?: string): boolean {
    return change.sprints.length > 0 &&
        !change.sprints.some((sprint) =>
            sprint._id === currentSprintId || sprint.startAt.isAfter(moment())
        ) &&
        !isWaitingOnOthers(change);
}

function matchesSearch(change: Change, query: string, lang: string): boolean {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
        return true;
    }
    const slug = String(change.slug ?? '').toLowerCase();
    if (slug.includes(normalized)) {
        return true;
    }
    const localized = change.localizedTitle(lang);
    if (typeof localized === 'string' && localized.toLowerCase().includes(normalized)) {
        return true;
    }
    const titles = change.title && typeof change.title === 'object'
        ? Object.values(change.title)
        : [];
    return titles.some((value) =>
        typeof value === 'string' && value.toLowerCase().includes(normalized)
    );
}

function MyTasksFocus() {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();

    const [activeTab, setActiveTab] = useState<TasksTab>('todo');
    const [projectFilter, setProjectFilter] = useState<Value>('all');
    const [typeFilter, setTypeFilter] = useState<Value>('all');
    const [sortMode, setSortMode] = useState<MyTasksSortMode>(() => loadMyTasksSortMode());
    const [searchQuery, setSearchQuery] = useState('');
    const userPickedTab = useRef(false);

    const { user } = useAppSelector(state => state.auth);
    const { changes, loading } = useAppSelector(state => state.changes);
    const { current } = useAppSelector(state => state.sprints);
    const {
        modalChangeId,
        detailChange,
        openChangeModal,
        closeChangeModal,
    } = useDashboardChangeModal();

    useEffect(() => {
        dispatch(currentSprint());
    }, [dispatch]);

    useEffect(() => {
        if (!user) {
            return;
        }
        // Clear shared `state.changes` so project-wide lists (e.g. Commits)
        // cannot flash as "À faire (N)" before the assignee fetch returns.
        dispatch(resetChanges());
        dispatch(loadChanges(myTasksListParams(user._id)));
    }, [user, dispatch]);

    useEffect(() => {
        saveMyTasksSortMode(sortMode);
    }, [sortMode]);

    const applySort = (items: Change[], intelligentCompare?: (a: Change, b: Change) => number) => {
        const sorted = [...items];
        if (sortMode === 'newest' || sortMode === 'oldest') {
            return sorted.sort((a, b) => compareByCreatedAt(a, b, sortMode));
        }
        if (sortMode === 'priority') {
            return sorted.sort(compareByPriority);
        }
        if (sortMode === 'updated') {
            return sorted.sort(compareByUpdatedAt);
        }
        if (!intelligentCompare) {
            return sorted;
        }
        return sorted.sort(intelligentCompare);
    };

    const sprintChanges = useMemo(() => {
        if (!current?.changes?.length || !user?._id) {
            return [];
        }
        const groups = Sprint.groupChangesByUser(Sprint.sortChangesBy(current.changes, 'priority'));
        const items = (groups[user._id]?.changes ?? [])
            .filter((change: Change) => isMyTasksVisible(change))
            .filter((change: Change) => matchesSearch(change, searchQuery, i18n.language));
        // Keep sprint priority order for intelligent mode.
        return applySort(items);
    }, [current?.changes, user?._id, searchQuery, sortMode, i18n.language]);

    const overdueChanges = useMemo(() => {
        const items = (changes ?? [])
            .filter((change: Change) => isMyTasksVisible(change))
            .filter((change: Change) => {
                if (isWaitingOnOthers(change)) {
                    return false;
                }
                return isDeadlineOverdue(change) || isSprintSlip(change, current?._id);
            })
            .filter((change: Change) => matchesSearch(change, searchQuery, i18n.language));
        return applySort(items, compareOverdueChanges);
    }, [changes, current?._id, searchQuery, sortMode, i18n.language]);

    const visibleChanges = useMemo(() => {
        return (changes ?? []).filter((item: Change) => isMyTasksVisible(item));
    }, [changes]);

    const widgetRelevantChanges = useMemo(() => {
        return (changes ?? []).filter((item: Change) => isMyTasksWidgetRelevant(item));
    }, [changes]);

    const selectableProjects = useMemo(() => {
        const byId: Record<string, Project> = {};
        widgetRelevantChanges.forEach((item: Change) => {
            if (item.project?._id) {
                byId[item.project._id] = item.project;
            }
        });
        return Object.values(byId).sort((a, b) => a.name.localeCompare(b.name));
    }, [widgetRelevantChanges]);

    const selectableTypes = useMemo(() => {
        const rawTypes = widgetRelevantChanges.map((item: Change) => item.type).filter(Boolean) as string[];
        return Array.from(new Set(rawTypes)).sort();
    }, [widgetRelevantChanges]);

    const filteredChanges = useMemo(() => {
        return visibleChanges
            .filter((item: Change) => projectFilter === 'all' || item.project?._id === projectFilter)
            .filter((item: Change) => typeFilter === 'all' || item.type === typeFilter)
            .filter((item: Change) => matchesSearch(item, searchQuery, i18n.language));
    }, [visibleChanges, projectFilter, typeFilter, searchQuery, i18n.language]);

    const replyChanges = useMemo(() => {
        const items = (changes ?? [])
            .filter((change: Change) => isClientReplyPending(change))
            .filter((item: Change) => projectFilter === 'all' || item.project?._id === projectFilter)
            .filter((item: Change) => typeFilter === 'all' || item.type === typeFilter)
            .filter((item: Change) => matchesSearch(item, searchQuery, i18n.language));
        return applySort(items, compareByUpdatedAt);
    }, [changes, projectFilter, typeFilter, searchQuery, sortMode, i18n.language]);

    const todoChanges = useMemo(() => {
        return applySort(
            filteredChanges.filter((change: Change) => isTodoStatus(change)),
            compareTodoChanges
        );
    }, [filteredChanges, sortMode]);

    const followChanges = useMemo(() => {
        return applySort(
            filteredChanges.filter((change: Change) => isFollowStatus(change)),
            compareAllChanges
        );
    }, [filteredChanges, sortMode]);

    const allChanges = useMemo(() => {
        return applySort(filteredChanges, compareAllChanges);
    }, [filteredChanges, sortMode]);

    const tabChanges: Record<TasksTab, Change[]> = {
        replies: replyChanges,
        todo: todoChanges,
        follow: followChanges,
        sprint: sprintChanges,
        overdue: overdueChanges,
        all: allChanges,
    };

    const tabCounts: Record<TasksTab, number> = {
        replies: replyChanges.length,
        todo: todoChanges.length,
        follow: followChanges.length,
        sprint: sprintChanges.length,
        overdue: overdueChanges.length,
        all: allChanges.length,
    };

    useEffect(() => {
        // Prefer "Réponses en attente", then "À faire"; else first non-empty tab.
        if (!userPickedTab.current) {
            if (replyChanges.length > 0) {
                if (activeTab !== 'replies') {
                    setActiveTab('replies');
                }
                return;
            }
            if (todoChanges.length > 0) {
                if (activeTab !== 'todo') {
                    setActiveTab('todo');
                }
                return;
            }
            const fallback = TABS.find((tab) => tabCounts[tab] > 0);
            if (fallback && activeTab !== fallback) {
                setActiveTab(fallback);
            }
            return;
        }

        if (tabCounts[activeTab] > 0) {
            return;
        }

        const fallback = TABS.find((tab) => tabCounts[tab] > 0);
        if (fallback) {
            setActiveTab(fallback);
        }
    }, [
        activeTab,
        replyChanges.length,
        todoChanges.length,
        tabCounts.replies,
        tabCounts.todo,
        tabCounts.follow,
        tabCounts.sprint,
        tabCounts.overdue,
        tabCounts.all,
    ]);

    const isLoadingTasks = Boolean(user) && isMyTasksListLoading(loading, changes?.length ?? 0);

    if (isLoadingTasks) {
        return <WidgetSkeleton rows={5} />;
    }

    const hasAnyAssignedTask = widgetRelevantChanges.length > 0 || sprintChanges.length > 0;
    if (!hasAnyAssignedTask) {
        return null;
    }

    const displayedChanges = tabChanges[activeTab];
    const showFilters = activeTab === 'replies' || activeTab === 'todo' || activeTab === 'follow' || activeTab === 'all';

    const onRemoveCard = (_removed: AnyObj) => {
        // Soft-delete is handled inside ChangeModal; refresh the list.
        closeChangeModal();
        if (user)
            dispatch(loadChanges(myTasksListParams(user._id)));
    };

    const typeLabel = (type: string) => t(`projects.${type}`) || type;

    const bulkActionsHref = user
        ? buildBulkActionsHref({
            assigneeId: user._id,
            tab: activeTab,
            projectFilter,
            typeFilter,
        })
        : '/changes';

    return (
        <div id="my-tasks" className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                <div className="flex items-end justify-between gap-3">
                    <h3 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white">
                        <Icon type="commit" size={6} className="mr-1.5" />
                        {t('dashboard.my_tasks')}
                    </h3>
                    {user && (
                        <Link
                            to={bulkActionsHref}
                            className="text-xs text-indigo-600 hover:underline"
                        >
                            {t('dashboard.bulk_actions_link')}
                            <Icon type="right" className="inline" />
                        </Link>
                    )}
                </div>
                <div className="mt-3 flex flex-wrap gap-1">
                    {TABS.map((tab) => {
                        if (tab === 'replies' && !replyChanges.length) {
                            return null;
                        }
                        if (tab === 'todo' && !todoChanges.length) {
                            return null;
                        }
                        if (tab === 'follow' && !followChanges.length) {
                            return null;
                        }
                        if (tab === 'sprint' && !sprintChanges.length) {
                            return null;
                        }
                        if (tab === 'overdue' && !overdueChanges.length) {
                            return null;
                        }
                        const isActive = activeTab === tab;
                        const isOverdue = tab === 'overdue';
                        const isReplies = tab === 'replies';
                        return (
                            <button
                                key={tab}
                                type="button"
                                onClick={() => {
                                    userPickedTab.current = true;
                                    setActiveTab(tab);
                                }}
                                className={`inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition ${
                                    isActive
                                        ? isOverdue
                                            ? 'bg-red-100 text-red-800'
                                            : isReplies
                                                ? 'bg-amber-100 text-amber-900'
                                                : 'bg-indigo-100 text-indigo-800'
                                        : isReplies
                                            ? 'text-amber-800 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/40'
                                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                                }`}
                            >
                                {isReplies && (
                                    <Icon type="warning" size={4} className="mr-1" color="amber-800" />
                                )}
                                {t(`dashboard.tab_${tab}`)}
                                {tabCounts[tab] > 0 && (
                                    <span className="ml-1 opacity-75">({tabCounts[tab]})</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="space-y-2 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                <div className="relative">
                    <input
                        type="search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('dashboard.my_tasks_search') as string}
                        className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 pr-10 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    />
                    <Icon type="search" className="pointer-events-none absolute top-2.5 right-3" size={5} color="gray-400" />
                </div>
                <div className={`grid grid-cols-1 gap-2 ${showFilters ? 'md:grid-cols-3' : 'md:grid-cols-1'}`}>
                    {showFilters && (
                        <>
                            <Select
                                onChange={setProjectFilter}
                                defaultValue={projectFilter}
                                placeholder={t('priorities.filters.project') as string}
                                options={[
                                    { value: 'all', label: t('priorities.filters.all_projects') || '' },
                                    ...selectableProjects.map((item: Project) => ({ value: item._id, label: item.name })),
                                ]}
                            />
                            <Select
                                onChange={setTypeFilter}
                                defaultValue={typeFilter}
                                placeholder={t('priorities.filters.type') as string}
                                options={[
                                    { value: 'all', label: t('priorities.filters.all_types') || '' },
                                    ...selectableTypes.map((item: string) => ({
                                        value: item,
                                        label: typeLabel(item),
                                    })),
                                ]}
                            />
                        </>
                    )}
                    <Select
                        onChange={(value) => setSortMode((value as MyTasksSortMode) || DEFAULT_MY_TASKS_SORT)}
                        defaultValue={sortMode}
                        placeholder={t('dashboard.my_tasks_sort.label') as string}
                        options={SORT_MODES.map((mode) => ({
                            value: mode,
                            label: t(`dashboard.my_tasks_sort.${mode}`) || mode,
                        }))}
                    />
                </div>
            </div>

            {activeTab === 'sprint' && current?.name && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 border-b border-gray-200 px-4 py-2 text-xs text-gray-500 dark:text-gray-400 dark:border-gray-700">
                    <span>
                        <Icon type="list" size={3} className="mr-1 inline" />
                        {current.getEstimatedHours(null, user?._id).toFixed(1)}{t('changes.estimate_units_min')}
                    </span>
                    <span>
                        <Icon type="check" size={3} className="mr-1 inline" />
                        {current.getRealisedHours(moment(), null, user?._id).toFixed(1)}{t('changes.estimate_units_min')}
                    </span>
                    <span>
                        <Icon type="todo" size={3} className="mr-1 inline" />
                        {current.getRemainingHours(moment(), null, user?._id).toFixed(1)}{t('changes.estimate_units_min')}
                    </span>
                </div>
            )}

            <div className={`max-h-[min(70vh,56rem)] space-y-2 overflow-y-auto p-4 ${
                activeTab === 'overdue'
                    ? 'bg-red-50 dark:bg-red-950/20'
                    : activeTab === 'replies'
                        ? 'bg-amber-50 dark:bg-amber-950/20'
                        : ''
            }`}>
                {displayedChanges.length === 0 && (
                    <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        {t('dashboard.you_have_no_tasks')}
                    </p>
                )}
                {displayedChanges.map((change: Change) => (
                    <ChangeCard
                        key={change._id}
                        change={change}
                        showProject
                        showSprint={activeTab !== 'sprint'}
                        onClick={() => openChangeModal(change)}
                    />
                ))}
            </div>

            {detailChange && detailChange._id === modalChangeId && detailChange.project?.name && (
                <ChangeModal
                    project={detailChange.project}
                    change={detailChange}
                    show={true}
                    showProjectName={true}
                    onClose={closeChangeModal}
                    onRemove={onRemoveCard}
                />
            )}
        </div>
    );
}

export default MyTasksFocus;
